import apiClient from '../../../services/apiClient';
import cacheService from '../../../services/cacheService';
import { pollPromise } from '../../../utils/pollPromise';

type LicenseWithSource = {
  source?: {
    previousSubscriptionID?: string;
  };
  subscription?: {
    id?: string;
  };
};

/**
 * Polls org licenses until one has source.previousSubscriptionID set for the
 * subscription that was just changed (either on the old license pointing at the
 * upcoming subscription, or on the new license pointing at the previous one).
 */
export const pollForLicenseWithPreviousSubscription = (
  orgId: string,
  subscriptionId: string,
  times = 12,
  interval = 2_500
): Promise<void> => {
  const endpoint = `/v1/licenses?detail=true&orgId=${orgId}`;

  return new Promise((resolve, reject) => {
    let found = false;

    const cancel = pollPromise(
      () => {
        if (found) {
          return Promise.resolve();
        }

        cacheService.removeAllInPath('/v1/licenses');

        return apiClient.get(endpoint).then(response => {
          const licenses = (response.data as { licenses: LicenseWithSource[] })?.licenses ?? [];
          const hasPreviousSubscription = licenses.some(
            license =>
              !!license.source?.previousSubscriptionID &&
              (license.subscription?.id === subscriptionId || license.source.previousSubscriptionID === subscriptionId)
          );

          if (hasPreviousSubscription) {
            found = true;
            cancel();
            resolve();
          }
        });
      },
      () => {
        if (!found) {
          reject(
            new Error(
              `License with previousSubscriptionID was not found after ${times} attempts for subscription ${subscriptionId}`
            )
          );
        }
      },
      times,
      interval
    );
  });
};
